import React from 'react';
import { Reveal } from '../components/Reveal';
import { BookOpen, FileSignature } from 'lucide-react';

/**
 * "What we can show you today" -- marketing ruling M1 (2026-10-20), Phase A.
 *
 * No live record, no simulated result, no Sharpe (R43(i), R52-2). The old
 * sample-fixture card and the /sample-backtest walkthrough were removed with
 * the backtesting product (M1 delete list, items 20-25).
 *
 * Dropped from the ruling's draft, [verify] failed: "the record of every
 * proposal, approval and order on your paper account". The product shows
 * proposals on the proposal page and recent fills on a deployment, but no
 * screen today lists orders (including refused ones) alongside proposals and
 * approvals. Returns when that screen exists.
 *
 * Phase B (flag LANDING_PHASE_B, after counsel) adds the measured table and
 * the five R52 sentences here. Not in this file until then.
 */
const inspectable = [
  {
    icon: BookOpen,
    title: 'The library page',
    body: 'Each rule with its source, the range its source says to expect, and how far we have checked it ourselves.',
  },
  {
    icon: FileSignature,
    title: 'Your mandate, in plain words',
    body: 'Your limits, what the portfolio may hold, and the list of what the agent may never do.',
  },
];

export const ProofSection: React.FC = () => {
  return (
    <section id="proof" className="py-28 px-6 scroll-mt-20">
      <div className="max-w-[900px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-sm text-[#38bdf8] font-medium uppercase tracking-widest mb-4">
            What we can show you today
          </p>
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            We have no live record.{' '}
            <span className="text-gradient">We will not show you one until we do.</span>
          </h2>
          <p className="text-white/50 text-lg mt-4 max-w-[620px] mx-auto leading-relaxed">
            Before you put any money in, you can inspect:
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {inspectable.map((item, i) => (
            <Reveal
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
            >
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center mb-4 text-white/60">
                <item.icon size={20} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{item.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
